export default class miniMap {
   constructor() {
      this.canvas = null;
      this.ctx = null;
      this.articles = null;
      this.baseScale = null;
      this.isPointerDown = false;
      this.mapTransform = null;

      this.init = this.init.bind(this);
      this.update = this.update.bind(this);
      this.draw = this.draw.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.panMainViewToPointer = this.panMainViewToPointer.bind(this);

      this.init();
   }

   init() {
      const t = this;
      console.log('MiniMap class initialized');
      if (!document.querySelector('.boxes-inner')) {
         setTimeout(() => {
            this.init();
         }, 500);
         return;
      }
      this.boxesInner = document.querySelector('.boxes-inner');
      this.canvas = document.getElementById('minimap');
      this.ctx = this.canvas.getContext('2d');
      this.main = document.querySelector('main');
      this.articles = document.querySelectorAll('.box');
      this.baseScale = 0.03;

      window.minimapPause = false;
      t.timeouts = [];

      window.addEventListener('resize', t.update);
      this.canvas.addEventListener('pointerdown', t.handlePointerDown);
      window.addEventListener('pointermove', t.handlePointerMove);
      window.addEventListener('pointerup', t.handlePointerUp);
      window.addEventListener('pointercancel', t.handlePointerUp);

      this.update(true);
   }

   update(force = false) {
      if (!force) {
         if (this._updateQueued) return;
         this._updateQueued = true;
         requestAnimationFrame(() => {
            this._updateQueued = false;
            this.draw();
         });
         return;
      }
      this.draw();
   }

   draw() {
      const t = this;
      if (!t.boxesInner) return;
      t.articles = document.querySelectorAll('.box');
      const nextWidth = t.boxesInner.getBoundingClientRect().width * t.baseScale;
      const nextHeight = t.boxesInner.getBoundingClientRect().height * t.baseScale;
      if (t.canvas.width !== nextWidth) t.canvas.width = nextWidth;
      if (t.canvas.height !== nextHeight) t.canvas.height = nextHeight;
      // Clear canvas
      t.ctx.clearRect(0, 0, t.canvas.width, t.canvas.height);

      const worldRects = [];
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const mainScale = Math.max(0.001, window.mainView.state.scale);
      const boxesRect = t.boxesInner.getBoundingClientRect();

      const isVisible = (el) => {
         if (!el) return false;
         if (el.classList.contains('non-match')) return false;
         if (el.style.display === 'none') return false;
         if (window.getComputedStyle(el).display === 'none') return false;
         const rect = el.getBoundingClientRect();
         return rect.width > 0 && rect.height > 0;
      };

      t.articles.forEach(article => {
         if (!isVisible(article)) return;
         // Use rendered rects normalized by current main scale.
         // This avoids relying on per-item transform internals from Isotope.
         const rect = article.getBoundingClientRect();
         const left = (rect.left - boxesRect.left) / mainScale;
         const top = (rect.top - boxesRect.top) / mainScale;
         const width = Math.max(1, rect.width / mainScale);
         const height = Math.max(1, rect.height / mainScale);
         const right = left + width;
         const bottom = top + height;
         worldRects.push({ article, left, top, width, height, right, bottom });
         if (left < minX) minX = left;
         if (top < minY) minY = top;
         if (right > maxX) maxX = right;
         if (bottom > maxY) maxY = bottom;
      });

      if (!isFinite(minX) || !isFinite(minY)) {
         return;
      }

      const computeBounds = (rects) => {
         let bMinX = Infinity;
         let bMinY = Infinity;
         let bMaxX = -Infinity;
         let bMaxY = -Infinity;
         rects.forEach(({ left, top, right, bottom }) => {
            if (left < bMinX) bMinX = left;
            if (top < bMinY) bMinY = top;
            if (right > bMaxX) bMaxX = right;
            if (bottom > bMaxY) bMaxY = bottom;
         });
         return { minX: bMinX, minY: bMinY, maxX: bMaxX, maxY: bMaxY };
      };

      // Guard against occasional outlier positions creating huge empty minimap.
      // If the drawn fit is suspiciously tiny, switch to robust quantile bounds.
      const maybeUseRobustBounds = () => {
         if (worldRects.length < 20) return { minX, minY, maxX, maxY };
         const worldWidth0 = Math.max(1, maxX - minX);
         const worldHeight0 = Math.max(1, maxY - minY);
         const fillX = t.canvas.width / worldWidth0;
         const fillY = t.canvas.height / worldHeight0;
         const tinyFit = fillX < 0.015 || fillY < 0.015;
         if (!tinyFit) return { minX, minY, maxX, maxY };

         const byCenterX = [...worldRects].sort((a, b) => ((a.left + a.right) * 0.5) - ((b.left + b.right) * 0.5));
         const byCenterY = [...worldRects].sort((a, b) => ((a.top + a.bottom) * 0.5) - ((b.top + b.bottom) * 0.5));
         const qLow = Math.floor(worldRects.length * 0.05);
         const qHigh = Math.ceil(worldRects.length * 0.95) - 1;
         const minCX = (byCenterX[Math.max(0, qLow)].left + byCenterX[Math.max(0, qLow)].right) * 0.5;
         const maxCX = (byCenterX[Math.max(0, qHigh)].left + byCenterX[Math.max(0, qHigh)].right) * 0.5;
         const minCY = (byCenterY[Math.max(0, qLow)].top + byCenterY[Math.max(0, qLow)].bottom) * 0.5;
         const maxCY = (byCenterY[Math.max(0, qHigh)].top + byCenterY[Math.max(0, qHigh)].bottom) * 0.5;

         const filtered = worldRects.filter(r => {
            const cx = (r.left + r.right) * 0.5;
            const cy = (r.top + r.bottom) * 0.5;
            return cx >= minCX && cx <= maxCX && cy >= minCY && cy <= maxCY;
         });

         if (filtered.length < Math.max(8, Math.floor(worldRects.length * 0.5))) {
            return { minX, minY, maxX, maxY };
         }
         return computeBounds(filtered);
      };

      const robust = maybeUseRobustBounds();
      minX = robust.minX;
      minY = robust.minY;
      maxX = robust.maxX;
      maxY = robust.maxY;

      const paddingX = 120;
      const paddingY = 40;
      const worldWidth = Math.max(1, maxX - minX);
      const worldHeight = Math.max(1, maxY - minY);
      const scaleX = t.canvas.width / (worldWidth + paddingX * 2);
      const scaleY = t.canvas.height / (worldHeight + paddingY * 2);
      const mapScale = Math.max(0.001, Math.min(scaleX, scaleY));
      const drawnWidth = worldWidth * mapScale;
      const drawnHeight = worldHeight * mapScale;
      // Center sparse layouts (image view), top-bias tall layouts (text view).
      const offsetX = Math.max(6, (t.canvas.width - drawnWidth) * 0.5);
      const freeY = Math.max(0, t.canvas.height - drawnHeight);
      const isTallContent = drawnHeight > t.canvas.height * 0.65;
      const offsetY = isTallContent
         ? Math.max(6, Math.min(16, freeY - 6))
         : Math.max(6, freeY * 0.5);
      t.mapTransform = { minX, minY, mapScale, offsetX, offsetY };

      const toMapX = (x) => (x - minX) * mapScale + offsetX;
      const toMapY = (y) => (y - minY) * mapScale + offsetY;
      const hasActiveRelations = !!(window.relators && window.relators.length > 0);

      // Draw each element's position on the mini-map
      worldRects.forEach(({ article, left, top, width, height }) => {
         const x = toMapX(left);
         const y = toMapY(top);
         const w = Math.max(1, width * mapScale);
         const h = Math.max(1, height * mapScale);
         let ac = article.classList;
         t.ctx.fillStyle = '#555';
         if (ac.contains('open') || (hasActiveRelations && (ac.contains('hovering') || ac.contains('related')))) {
            t.ctx.fillStyle = '#fff';
         }
         t.ctx.fillRect(x, y, w, h);
      });

      const viewportWorldWidth = window.innerWidth / mainScale;
      const viewportWorldHeight = window.innerHeight / mainScale;
      const viewportCenterX = (window.innerWidth / 2 - boxesRect.left) / mainScale;
      const viewportCenterY = (window.innerHeight / 2 - boxesRect.top) / mainScale;
      const viewportX = toMapX(viewportCenterX - viewportWorldWidth / 2);
      const viewportY = toMapY(viewportCenterY - viewportWorldHeight / 2);
      const viewportWidth = Math.max(1, viewportWorldWidth * mapScale);
      const viewportHeight = Math.max(1, viewportWorldHeight * mapScale);
      t.ctx.strokeStyle = '#f7bdc3';
      t.ctx.strokeRect(viewportX, viewportY, viewportWidth, viewportHeight);
   }

   panMainViewToPointer(event) {
      if (!this.mapTransform) return;
      const rect = this.canvas.getBoundingClientRect();
      const xCss = event.clientX - rect.left;
      const yCss = event.clientY - rect.top;
      // Convert CSS pixels to canvas coordinate space.
      const x = xCss * (this.canvas.width / Math.max(1, rect.width));
      const y = yCss * (this.canvas.height / Math.max(1, rect.height));
      const { minX, minY, mapScale, offsetX, offsetY } = this.mapTransform;
      const targetWorldX = ((x - offsetX) / mapScale) + minX;
      const targetWorldY = ((y - offsetY) / mapScale) + minY;
      const mainScale = window.mainView.state.scale;
      const boxesRect = this.boxesInner.getBoundingClientRect();
      const currentWorldX = (window.innerWidth / 2 - boxesRect.left) / mainScale;
      const currentWorldY = (window.innerHeight / 2 - boxesRect.top) / mainScale;
      const deltaWorldX = targetWorldX - currentWorldX;
      const deltaWorldY = targetWorldY - currentWorldY;
      window.mainView.state.translateX -= deltaWorldX * mainScale;
      window.mainView.state.translateY -= deltaWorldY * mainScale;
      window.mainView.updateTransform();
      this.update();
   }

   handlePointerDown(event) {
      if (!event.target || event.target !== this.canvas) return;
      this.isPointerDown = true;
      this.panMainViewToPointer(event);
   }

   handlePointerMove(event) {
      if (!this.isPointerDown) return;
      this.panMainViewToPointer(event);
   }

   handlePointerUp() {
      this.isPointerDown = false;
   }
}